// @seventide/types — money module DTOs (FR-3.1–FR-3.6, FR-8.1).
// Owned by the money module agent (see docs/ARCHITECTURE.md §6).
// Shared enums (AccountType, TransactionCategory, sub-categories) live in ./enums.
import { z } from 'zod';
import { Id, IsoDate, IsoDateTime, Pence } from './common';
import {
  AccountType,
  INCOME_SUBCATEGORIES,
  LIVING_EXPENSE_SUBCATEGORIES,
  TransactionCategory,
  type AccountType as AccountTypeT,
  type TransactionCategory as TransactionCategoryT,
} from './enums';

// ---------------------------------------------------------------- labels

export const ACCOUNT_TYPE_LABELS: Record<AccountTypeT, string> = {
  bank: 'Bank account',
  savings: 'Savings account',
  isa: 'ISA',
  investments: 'Investments',
  pension: 'Pension',
  property: 'Property',
  other_asset: 'Other asset',
  debt: 'Debt',
};

/** Account groups used on Money › Accounts. */
export const ACCOUNT_GROUPS: { key: string; label: string; types: AccountTypeT[] }[] = [
  { key: 'cash', label: 'Bank and savings', types: ['bank', 'savings', 'isa'] },
  { key: 'investments', label: 'Investments and pensions', types: ['investments', 'pension'] },
  { key: 'assets', label: 'Property and other assets', types: ['property', 'other_asset'] },
  { key: 'debts', label: 'Debts', types: ['debt'] },
];

/**
 * Why an account is left out of the council's means test (FR-3.1 `disregardReason`). Plain labels only:
 * the rules themselves are explained by the care-* explanation cards.
 */
export const DISREGARD_REASONS = [
  'main_home',
  'qualifying_relative_in_home',
  'twelve_week_disregard',
  'pension_not_in_payment',
  'personal_possessions',
  'life_insurance',
  'compensation',
  'held_for_someone_else',
  'other',
] as const;
export const DisregardReason = z.enum(DISREGARD_REASONS);
export type DisregardReason = z.infer<typeof DisregardReason>;
export const DISREGARD_REASON_LABELS: Record<DisregardReason, string> = {
  main_home: 'Main home (the council looks at the home separately)',
  qualifying_relative_in_home: 'Main home where a partner or qualifying relative still lives',
  twelve_week_disregard: 'Main home during the first 12 weeks of permanent care',
  pension_not_in_payment: 'Pension not yet being paid out',
  personal_possessions: 'Personal possessions',
  life_insurance: 'Life insurance policy',
  compensation: 'Personal injury compensation',
  held_for_someone_else: 'Money held for someone else',
  other: 'Another reason',
};

export const BALANCE_SOURCES = ['manual', 'csv', 'open_banking'] as const;
export const BalanceSource = z.enum(BALANCE_SOURCES);
export type BalanceSource = z.infer<typeof BalanceSource>;
export const BALANCE_SOURCE_LABELS: Record<BalanceSource, string> = {
  manual: 'Entered by hand',
  csv: 'From an imported statement',
  open_banking: 'From a bank connection',
};

export const TransactionDirection = z.enum(['in', 'out']);
export type TransactionDirection = z.infer<typeof TransactionDirection>;

/** Valid sub-categories per category (FR-3.3). Only income and living expenses have sub-categories. */
export const TRANSACTION_SUBCATEGORIES: Partial<Record<TransactionCategoryT, readonly string[]>> = {
  income: INCOME_SUBCATEGORIES,
  living_expenses: LIVING_EXPENSE_SUBCATEGORIES,
};

/** True if `sub` is allowed for `category` (null is always allowed). */
export function isValidSubCategory(category: TransactionCategoryT | null | undefined, sub: string | null | undefined): boolean {
  if (sub === null || sub === undefined || sub === '') return true;
  if (!category) return false;
  return (TRANSACTION_SUBCATEGORIES[category] ?? []).includes(sub);
}

const trimmed = (max: number) => z.string().trim().max(max);
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v === '' ? null : v));

// ---------------------------------------------------------------- accounts (FR-3.1, FR-3.4)

export const AccountBalance = z.object({
  id: Id,
  amountPence: Pence,
  asOf: IsoDate,
  source: BalanceSource,
});
export type AccountBalance = z.infer<typeof AccountBalance>;

export const AccountDto = z.object({
  id: Id,
  type: AccountType,
  provider: z.string().nullable(),
  /** Decrypted display name, e.g. "Current account". */
  name: z.string().nullable(),
  /** Decrypted masked number, e.g. "••4821". */
  numberMasked: z.string().nullable(),
  /** Counted in the council's means test (care capital). */
  isAssessable: z.boolean(),
  disregardReason: DisregardReason.nullable(),
  isJoint: z.boolean(),
  /** Principal's share, 0–100. */
  ownershipPct: z.number(),
  openedOn: IsoDate.nullable(),
  closedOn: IsoDate.nullable(),
  notes: z.string().nullable(),
  latestBalance: AccountBalance.nullable(),
  /** Days since the latest balance (null when none). */
  daysSinceBalance: z.number().int().nullable(),
  /** FR-3.4: no balance recorded within the prompt period (rule set `app.balancePromptDays`). */
  needsUpdate: z.boolean(),
  transactionCount: z.number().int(),
  /** Transactions dated after the latest balance. */
  transactionsSinceBalance: z.number().int(),
  /** Latest balance adjusted by transactions dated after it (null when no balance). */
  estimatedBalancePence: Pence.nullable(),
  /** R2 open banking — always false in R1. */
  openBankingConnected: z.boolean(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type AccountDto = z.infer<typeof AccountDto>;

export const AccountListResponse = z.object({
  items: z.array(AccountDto),
  /** Rule-set value used for the update prompt (FR-3.4). */
  balancePromptDays: z.number().int(),
  totals: z.object({
    /** Sum of the Principal's share of latest balances (debts subtracted). */
    netPence: Pence,
    assetsPence: Pence,
    debtsPence: Pence,
    /** Principal's share of balances counted in the means test. */
    assessablePence: Pence,
  }),
});
export type AccountListResponse = z.infer<typeof AccountListResponse>;

const AccountFields = z.object({
  type: AccountType,
  provider: optionalText(120),
  name: trimmed(120).min(1, 'Enter a name for the account, like "Current account"'),
  /** Last few characters only — never a full account number. */
  numberMasked: optionalText(24).refine((v) => !v || !/\d{6,}/.test(v.replace(/\s/g, '')), {
    message: 'Enter only the last 4 digits, not the full account number',
  }),
  isAssessable: z.boolean().default(true),
  disregardReason: DisregardReason.nullish(),
  isJoint: z.boolean().default(false),
  ownershipPct: z.number().min(0).max(100).default(100),
  openedOn: IsoDate.nullish(),
  closedOn: IsoDate.nullish(),
  notes: optionalText(2000),
});

export const CreateAccountRequest = AccountFields.extend({
  /** Optional client-generated id (offline / idempotent create). */
  id: Id.optional(),
  /** Optional first balance. */
  openingBalancePence: Pence.nullish(),
  openingBalanceAsOf: IsoDate.nullish(),
});
export type CreateAccountRequest = z.input<typeof CreateAccountRequest>;

export const UpdateAccountRequest = AccountFields.partial();
export type UpdateAccountRequest = z.input<typeof UpdateAccountRequest>;

export const BalanceSnapshotDto = z.object({
  id: Id,
  accountId: Id,
  asOf: IsoDate,
  amountPence: Pence,
  source: BalanceSource,
  documentId: Id.nullable(),
  importBatchId: Id.nullable(),
  createdByName: z.string().nullable(),
  createdAt: IsoDateTime,
});
export type BalanceSnapshotDto = z.infer<typeof BalanceSnapshotDto>;

export const CreateBalanceRequest = z.object({
  asOf: IsoDate,
  /** Integer pence. For debts, the amount owed. A bank account may be overdrawn (negative). */
  amountPence: Pence,
  documentId: Id.nullish(),
});
export type CreateBalanceRequest = z.input<typeof CreateBalanceRequest>;

// ---------------------------------------------------------------- people (FR-8.1 attribution)

export const MoneyPerson = z.object({
  userId: Id,
  name: z.string(),
  role: z.enum(['owner', 'attorney']),
});
export type MoneyPerson = z.infer<typeof MoneyPerson>;

// ---------------------------------------------------------------- transactions (FR-3.3, FR-8.1)

export const TransactionDto = z.object({
  id: Id,
  accountId: Id,
  date: IsoDate,
  /** Always ≥ 0; see `direction`. */
  amountPence: Pence,
  direction: TransactionDirection,
  category: TransactionCategory.nullable(),
  subCategory: z.string().nullable(),
  isCapitalReceipt: z.boolean(),
  reference: z.string().nullable(),
  counterparty: z.string().nullable(),
  notes: z.string().nullable(),
  madeByUserId: Id.nullable(),
  madeByName: z.string().nullable(),
  madeByAttorney: z.boolean(),
  importBatchId: Id.nullable(),
  documentIds: z.array(Id),
  /** FR-8.1 running balance of the account after this transaction (null when no balance is recorded). */
  runningBalancePence: Pence.nullable(),
  /** Gift record linked to this transaction (only when the viewer can see gifts). */
  giftId: Id.nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type TransactionDto = z.infer<typeof TransactionDto>;

export const UNCATEGORISED = 'uncategorised' as const;

const csvList = <T extends z.ZodTypeAny>(item: T) =>
  z.preprocess(
    (v) => (typeof v === 'string' ? v.split(',').filter(Boolean) : v),
    z.array(item).max(50),
  );

export const TransactionListQuery = z.object({
  accountId: csvList(Id).optional(),
  from: IsoDate.optional(),
  to: IsoDate.optional(),
  /** Categories; include "uncategorised" for rows without one. */
  category: csvList(z.union([TransactionCategory, z.literal(UNCATEGORISED)])).optional(),
  subCategory: z.string().max(40).optional(),
  direction: TransactionDirection.optional(),
  /** "attorney" = any attorney-made transaction, or a member's user id. */
  madeBy: z.union([z.literal('attorney'), Id]).optional(),
  importBatchId: Id.optional(),
  /** Free-text search over description/reference, counterparty and notes (case-insensitive). */
  q: z.string().trim().max(100).optional(),
  sort: z.enum(['date_desc', 'date_asc', 'amount_desc', 'amount_asc']).default('date_desc'),
  limit: z.coerce.number().int().min(1).max(5000).default(500),
  offset: z.coerce.number().int().min(0).default(0),
});
export type TransactionListQuery = z.input<typeof TransactionListQuery>;

export const TransactionListResponse = z.object({
  items: z.array(TransactionDto),
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int(),
  /** Totals over every matching row (not just this page). */
  totals: z.object({ inPence: Pence, outPence: Pence, uncategorised: z.number().int() }),
});
export type TransactionListResponse = z.infer<typeof TransactionListResponse>;

const TransactionFields = z.object({
  accountId: Id,
  date: IsoDate,
  amountPence: Pence.positive('Enter an amount greater than zero'),
  direction: TransactionDirection,
  category: TransactionCategory.nullish(),
  subCategory: z.string().trim().max(40).nullish(),
  isCapitalReceipt: z.boolean().default(false),
  reference: optionalText(300),
  counterparty: optionalText(200),
  notes: optionalText(2000),
  /** FR-8.1: made by an attorney on the Principal's behalf. */
  madeByAttorney: z.boolean().default(false),
  /** The acting attorney (owner or attorney member). Defaults to the caller when they are an attorney. */
  madeByUserId: Id.nullish(),
  documentIds: z.array(Id).max(20).optional(),
});

const subCategoryCheck = (v: { category?: TransactionCategoryT | null; subCategory?: string | null }) =>
  v.subCategory === undefined || isValidSubCategory(v.category ?? null, v.subCategory);

export const CreateTransactionRequest = TransactionFields.extend({
  /** Client-generated id (offline / idempotent create, ADR 0004). */
  id: Id.optional(),
  clientId: Id.optional(),
}).refine(subCategoryCheck, { message: 'Choose a type that matches the category', path: ['subCategory'] });
export type CreateTransactionRequest = z.input<typeof CreateTransactionRequest>;

export const UpdateTransactionRequest = TransactionFields.partial()
  .extend({ clientUpdatedAt: IsoDateTime.optional() })
  .refine(
    (v) => v.category === undefined || subCategoryCheck(v),
    { message: 'Choose a type that matches the category', path: ['subCategory'] },
  );
export type UpdateTransactionRequest = z.input<typeof UpdateTransactionRequest>;

/** FR-3.2 bulk categorise / FR-8.1 bulk attribution. Only the fields given are changed. */
export const BulkTransactionRequest = z
  .object({
    ids: z.array(Id).min(1).max(5000),
    category: TransactionCategory.nullable().optional(),
    subCategory: z.string().trim().max(40).nullable().optional(),
    isCapitalReceipt: z.boolean().optional(),
    madeByAttorney: z.boolean().optional(),
    madeByUserId: Id.nullable().optional(),
    /** Remember the category for these counterparties in future imports (default true). */
    remember: z.boolean().default(true),
  })
  .refine((v) => v.category === undefined || isValidSubCategory(v.category, v.subCategory ?? null), {
    message: 'Choose a type that matches the category',
    path: ['subCategory'],
  });
export type BulkTransactionRequest = z.input<typeof BulkTransactionRequest>;

export const BulkTransactionResponse = z.object({ updated: z.number().int() });
export type BulkTransactionResponse = z.infer<typeof BulkTransactionResponse>;

export const MoneyExportRequest = z.object({
  format: z.enum(['pdf', 'csv']),
  accountId: z.array(Id).max(50).optional(),
  from: IsoDate.optional(),
  to: IsoDate.optional(),
  category: z.array(z.union([TransactionCategory, z.literal(UNCATEGORISED)])).max(20).optional(),
  madeBy: z.union([z.literal('attorney'), Id]).optional(),
});
export type MoneyExportRequest = z.input<typeof MoneyExportRequest>;

// ---------------------------------------------------------------- CSV import (FR-3.2)

export const CSV_DATE_FORMATS = [
  'dd/MM/yyyy',
  'dd/MM/yy',
  'yyyy-MM-dd',
  'dd-MM-yyyy',
  'dd.MM.yyyy',
  'd MMM yyyy',
  'dd-MMM-yy',
  'dd-MMM-yyyy',
  'MM/dd/yyyy',
] as const;
export const CsvDateFormat = z.enum(CSV_DATE_FORMATS);
export type CsvDateFormat = z.infer<typeof CsvDateFormat>;
export const CSV_DATE_FORMAT_LABELS: Record<CsvDateFormat, string> = {
  'dd/MM/yyyy': 'Day/month/year, e.g. 12/10/2026',
  'dd/MM/yy': 'Day/month/short year, e.g. 12/10/26',
  'yyyy-MM-dd': 'Year-month-day, e.g. 2026-10-12',
  'dd-MM-yyyy': 'Day-month-year, e.g. 12-10-2026',
  'dd.MM.yyyy': 'Day.month.year, e.g. 12.10.2026',
  'd MMM yyyy': 'Day month-name year, e.g. 12 Oct 2026',
  'dd-MMM-yy': 'Day-month-name-short year, e.g. 12-Oct-26',
  'dd-MMM-yyyy': 'Day-month-name-year, e.g. 12-Oct-2026',
  'MM/dd/yyyy': 'Month/day/year (American), e.g. 10/12/2026',
};

export const CsvDelimiter = z.enum([',', ';', '\t', '|']);
export type CsvDelimiter = z.infer<typeof CsvDelimiter>;

const ColumnIndex = z.number().int().min(0).max(200);

/** How the columns of a bank CSV map to transaction fields (saved per bank in csv_import_layouts). */
export const CsvMapping = z
  .object({
    delimiter: CsvDelimiter,
    /** Lines to skip before the header (or the first data row). */
    skipRows: z.number().int().min(0).max(50).default(0),
    hasHeader: z.boolean().default(true),
    dateFormat: CsvDateFormat,
    amountMode: z.enum(['signed', 'debit_credit']),
    /** For 'signed': true when money going out is shown as a positive number (credit-card style). */
    invertSign: z.boolean().default(false),
    columns: z.object({
      date: ColumnIndex,
      description: ColumnIndex.nullish(),
      reference: ColumnIndex.nullish(),
      counterparty: ColumnIndex.nullish(),
      amount: ColumnIndex.nullish(),
      debit: ColumnIndex.nullish(),
      credit: ColumnIndex.nullish(),
      balance: ColumnIndex.nullish(),
    }),
  })
  .refine((m) => (m.amountMode === 'signed' ? m.columns.amount != null : m.columns.debit != null || m.columns.credit != null), {
    message: 'Choose the column (or columns) that hold the amount',
    path: ['columns'],
  })
  .refine((m) => m.columns.description != null || m.columns.reference != null || m.columns.counterparty != null, {
    message: 'Choose the column that describes each payment',
    path: ['columns', 'description'],
  });
export type CsvMapping = z.infer<typeof CsvMapping>;
export type CsvMappingInput = z.input<typeof CsvMapping>;

export const ImportUploadResponse = z.object({
  batchId: Id,
  accountId: Id,
  filename: z.string(),
  encoding: z.string(),
  /** Detected layout of the file (also inside `mapping` when one was found). */
  delimiter: CsvDelimiter,
  skipRows: z.number().int(),
  hasHeader: z.boolean(),
  /** Header cells (or "Column 1…" when the file has no header). */
  columns: z.array(z.string()),
  /** First raw rows after the header, for the mapping screen. */
  sampleRows: z.array(z.array(z.string())),
  /** The first lines of the file as cells (including any preamble), so the wizard can re-split them when the
   * person changes the header row or the number of lines to skip. */
  rawHead: z.array(z.array(z.string())),
  rowCount: z.number().int(),
  /** Suggested mapping from a saved layout, a known bank or a best guess. */
  mapping: CsvMapping.nullable(),
  mappingSource: z.enum(['saved_layout', 'known_bank', 'guess', 'none']),
  /** e.g. "Lloyds Bank" or the saved layout's name. */
  bankName: z.string().nullable(),
  layoutId: Id.nullable(),
});
export type ImportUploadResponse = z.infer<typeof ImportUploadResponse>;

export const SUGGESTION_SOURCES = ['learned', 'keyword', 'none'] as const;
export type SuggestionSource = (typeof SUGGESTION_SOURCES)[number];

export const ImportPreviewRow = z.object({
  /** Row index in the file's data rows (0-based). */
  index: z.number().int(),
  date: IsoDate.nullable(),
  amountPence: Pence.nullable(),
  direction: TransactionDirection.nullable(),
  description: z.string().nullable(),
  counterparty: z.string().nullable(),
  balancePence: Pence.nullable(),
  /** Why the row can't be imported (plain English), or null. */
  error: z.string().nullable(),
  duplicate: z.boolean(),
  category: TransactionCategory.nullable(),
  subCategory: z.string().nullable(),
  isCapitalReceipt: z.boolean(),
  suggestionSource: z.enum(SUGGESTION_SOURCES),
});
export type ImportPreviewRow = z.infer<typeof ImportPreviewRow>;

export const ImportPreviewRequest = z.object({
  mapping: CsvMapping,
  /** Return only the first N rows (summary counts always cover the whole file). */
  limit: z.number().int().min(1).max(10000).optional(),
});
export type ImportPreviewRequest = z.input<typeof ImportPreviewRequest>;

export const ImportPreviewResponse = z.object({
  rows: z.array(ImportPreviewRow),
  summary: z.object({
    total: z.number().int(),
    ready: z.number().int(),
    duplicates: z.number().int(),
    errors: z.number().int(),
    dateFrom: IsoDate.nullable(),
    dateTo: IsoDate.nullable(),
    moneyInPence: Pence,
    moneyOutPence: Pence,
  }),
});
export type ImportPreviewResponse = z.infer<typeof ImportPreviewResponse>;

export const ImportCommitRequest = z.object({
  mapping: CsvMapping,
  /** Save (or update) this layout under a bank/provider name for next time. */
  saveLayoutAs: z.string().trim().min(1).max(80).nullish(),
  /** Per-row choices (rows not listed use the suggestion). */
  rows: z
    .array(
      z.object({
        index: z.number().int().min(0),
        include: z.boolean().default(true),
        category: TransactionCategory.nullable().optional(),
        subCategory: z.string().trim().max(40).nullable().optional(),
        isCapitalReceipt: z.boolean().optional(),
      }),
    )
    .max(10000)
    .default([]),
  /** Record the last balance in the file as a balance snapshot (default true). */
  recordBalance: z.boolean().default(true),
});
export type ImportCommitRequest = z.input<typeof ImportCommitRequest>;

export const ImportBatchDto = z.object({
  id: Id,
  accountId: Id,
  accountName: z.string().nullable(),
  filename: z.string().nullable(),
  bankName: z.string().nullable(),
  status: z.enum(['pending', 'previewed', 'imported', 'failed', 'undone']),
  rowCount: z.number().int(),
  importedCount: z.number().int(),
  duplicateCount: z.number().int(),
  skippedCount: z.number().int(),
  dateFrom: IsoDate.nullable(),
  dateTo: IsoDate.nullable(),
  /** Imported transactions still present (undo removes these). */
  remainingCount: z.number().int(),
  createdByName: z.string().nullable(),
  createdAt: IsoDateTime,
  importedAt: IsoDateTime.nullable(),
  undoneAt: IsoDateTime.nullable(),
});
export type ImportBatchDto = z.infer<typeof ImportBatchDto>;

export const ImportCommitResponse = z.object({
  batch: ImportBatchDto,
  imported: z.number().int(),
  duplicates: z.number().int(),
  skipped: z.number().int(),
  balanceRecorded: z.boolean(),
});
export type ImportCommitResponse = z.infer<typeof ImportCommitResponse>;

export const CsvLayoutDto = z.object({
  id: Id,
  bankName: z.string(),
  headerSignature: z.string().nullable(),
  mapping: CsvMapping,
  updatedAt: IsoDateTime,
});
export type CsvLayoutDto = z.infer<typeof CsvLayoutDto>;

// ---------------------------------------------------------------- Home summary (ARCHITECTURE §7)

export const MoneySummary = z.union([
  z.object({ available: z.literal(false) }),
  z.object({
    available: z.literal(true),
    /** Number of open accounts. */
    accounts: z.number().int(),
    /** Principal's share of the latest balances, debts subtracted. */
    totalBalancePence: Pence,
    /** FR-3.4: open accounts with no balance in the prompt period. */
    accountsNeedingUpdate: z.number().int(),
    uncategorisedCount: z.number().int(),
    lastBalanceDate: IsoDate.nullable(),
  }),
]);
export type MoneySummary = z.infer<typeof MoneySummary>;
